import { useEffect, useState } from 'react';
import type { PlatformPulse } from 'shared';
import { api } from './api';
import Icon, { type IconName } from './Icon';
import { useRealtime } from './realtime';

/** 1200 → 1,2 тыс. В шапке важен порядок величины, а не точное число. */
function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн`;
  if (n >= 10_000) return `${Math.round(n / 1000)} тыс.`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} тыс.`;
  return String(n);
}

/**
 * Три счётчика.
 *
 * У каждого есть подпись, а не только иконка: грузовик, коробка и человечки
 * рядом друг с другом читаются одинаково, и без слова рядом цифра ничего
 * не сообщает. Полная формулировка — в подсказке при наведении.
 */
const ITEMS: {
  key: keyof PlatformPulse;
  icon: IconName;
  label: string;
  title: string;
}[] = [
  {
    key: 'members',
    icon: 'users',
    label: 'участников',
    title: 'Участников, заходивших за последние 30 дней',
  },
  {
    key: 'inWork',
    icon: 'route',
    label: 'в рейсе',
    title: 'Грузов в работе: рейс начат, но ещё не завершён',
  },
  {
    key: 'open',
    icon: 'box',
    label: 'свободно',
    title: 'Свободных заявок: опубликованы и никем не взяты',
  },
];

/**
 * Пульс площадки в шапке.
 *
 * Пустая биржа и биржа с сотнями заявок выглядят одинаково, пока не сказать
 * цифру. Обновляется по событиям ленты, но не чаще раза в полминуты:
 * счётчики на сервере всё равно кэшируются.
 */
export default function Pulse() {
  const [data, setData] = useState<PlatformPulse | null>(null);

  useEffect(() => {
    let last = 0;
    let alive = true;

    const load = () => {
      if (Date.now() - last < 30_000) return;
      last = Date.now();

      api
        .pulse()
        .then((p) => {
          if (alive) setData(p);
        })
        .catch(() => {});
    };

    load();
    const timer = window.setInterval(load, 60_000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  // Появился или ушёл груз — числа устарели. Перезапрос дешёвый: на сервере
  // он читается из кэша.
  const refresh = () => {
    api
      .pulse()
      .then(setData)
      .catch(() => {});
  };

  useRealtime('cargo:new', refresh);
  useRealtime('cargo:closed', refresh);

  if (!data) return null;

  return (
    <div className="pulse" role="status" aria-label="Что происходит на площадке">
      {ITEMS.map((it) => (
        <span
          key={it.key}
          className="pulse__item"
          // tabIndex, чтобы подсказка открывалась и с клавиатуры: на телефоне
          // наведения нет, и title там не показывается вовсе.
          tabIndex={0}
          data-tip={it.title}
          // Последний счётчик стоит у правого края: подсказка по центру
          // уехала бы за окно.
          data-tip-align={it.key === 'open' ? 'end' : undefined}
          aria-label={`${it.title}: ${data[it.key]}`}
        >
          <Icon name={it.icon} size={14} />
          <b>{compact(data[it.key])}</b>
          <span className="pulse__label">{it.label}</span>
        </span>
      ))}
    </div>
  );
}
